import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Check } from 'lucide-react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const BASE_PRICE = 5;

const CardForm = () => {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!stripe || !elements) {
      return;
    }

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      const response = await fetch('/api/setup-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          payment_method_id: paymentMethod.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add payment method');
      }

      setSuccess(true);

      setTimeout(async () => {
        const { data, error } = await supabase.auth.refreshSession();

        if (data?.session) {
          setTimeout(() => {
            window.location.href = '/billing';
          }, 500);
        } else {
          console.error('Failed to refresh:', error);
          window.location.reload();
        }
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {success ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <p className="text-green-800 font-medium">Cartão adicionado com sucesso!</p>
        </div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Informação do Cartão
            </label>
            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <CardElement
                options={{
                  hidePostalCode: true,
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': { color: '#aab7c4' },
                    },
                    invalid: { color: '#9e2146' },
                  },
                }}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!stripe || loading}
            className="w-full py-3 bg-gradient-primary text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'A processar...' : 'Adicionar Cartão'}
          </button>

          <p className="text-xs text-gray-500 text-center">
            Pagamento seguro processado pelo Stripe.
          </p>
        </>
      )}
    </form>
  );
};

const BillingPage = () => {
  const { user } = useAuth();
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [charges, setCharges] = useState([]);
  const [loadingCharges, setLoadingCharges] = useState(true);
  const [removingCard, setRemovingCard] = useState(false);
  const [nif, setNif] = useState('');
  const [savingNif, setSavingNif] = useState(false);
  const [nifSaved, setNifSaved] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadCharges();
      setNif(user.nif || '');
    } else {
      setLoadingCharges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadCharges = async () => {
    try {
      const { data, error } = await supabase
        .from('Leads')
        .select('full_name, price, created_at, payment_status')
        .eq('payment_status', 'paid')
        .eq('claimed_by', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCharges(data || []);
    } catch (error) {
      console.error('Error loading charges:', error);
      setCharges([]);
    } finally {
      setLoadingCharges(false);
    }
  };

  const handleSaveNif = async () => {
    const trimmed = nif.trim();
    if (trimmed && !/^\d{9}$/.test(trimmed)) {
      alert('O NIF deve ter exactamente 9 dígitos.');
      return;
    }
    setSavingNif(true);
    try {
      const { error } = await supabase.auth.updateUser({ data: { nif: trimmed || null } });
      if (error) throw error;
      await supabase.auth.refreshSession();
      setNifSaved(true);
      setTimeout(() => setNifSaved(false), 3000);
    } catch (err) {
      alert('Erro ao guardar NIF: ' + err.message);
    } finally {
      setSavingNif(false);
    }
  };

  const handleRemoveCard = async () => {
    const confirmed = window.confirm(
      '⚠️ Remover Método de Pagamento?\n\n' +
      'Após remover o cartão, não poderá desbloquear novas leads até adicionar outro método de pagamento.\n\n' +
      'Tem a certeza que quer continuar?'
    );

    if (!confirmed) return;

    setRemovingCard(true);

    try {
      const response = await fetch('/api/remove-payment-method', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to remove payment method');
      }

      await supabase.auth.refreshSession();
      alert('✅ Cartão removido com sucesso!');
      window.location.reload();

    } catch (error) {
      console.error('Error removing card:', error);
      alert('❌ Erro ao remover cartão: ' + error.message);
    } finally {
      setRemovingCard(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Faturação</h1>

      {/* Informação de Preços */}
      <div className="bg-secondary/10 border-2 border-secondary/30 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Preço por lead</h3>
            <p className="text-sm text-gray-500">O valor pode oscilar de acordo com os custos de aquisição</p>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-primary">Preço variável</div>
            <div className="text-sm text-gray-600">por lead</div>
          </div>
        </div>
        <div className="flex items-start gap-2 text-sm text-gray-700">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <span>Será feita uma cobrança por cada pedido de contacto desbloqueado</span>
        </div>
      </div>

      {/* NIF */}
      <div className="bg-white rounded-xl shadow-md p-8 border border-primary/10 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Dados de Faturação</h2>
        <p className="text-sm text-gray-500 mb-6">Número de identificação fiscal para efeitos de faturação</p>
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">NIF</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={9}
              value={nif}
              onChange={e => setNif(e.target.value.replace(/\D/g, ''))}
              placeholder="123456789"
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm w-40 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <button
            onClick={handleSaveNif}
            disabled={savingNif}
            className="mt-5 px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {savingNif ? 'A guardar...' : 'Guardar'}
          </button>
          {nifSaved && (
            <div className="mt-5 flex items-center gap-1 text-green-700 text-sm">
              <Check className="w-4 h-4" />
              Guardado
            </div>
          )}
        </div>
      </div>

      {/* Formulário de Cartão */}
      <div className="bg-white rounded-xl shadow-md p-8 border border-primary/10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-secondary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {user?.hasPaymentMethod && !showUpdateForm
                ? 'Método de Pagamento'
                : user?.hasPaymentMethod
                  ? 'Atualizar Método de Pagamento'
                  : 'Adicionar Método de Pagamento'}
            </h2>
            <p className="text-sm text-gray-600">
              {user?.hasPaymentMethod && !showUpdateForm
                ? 'Cartão de crédito já associado à conta'
                : 'Adicione um cartão para começar a receber pedidos de contacto'
              }
            </p>
          </div>
        </div>

        {user?.hasPaymentMethod && !showUpdateForm ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-900">Cartão de pagamento ativo</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setShowUpdateForm(true)}
                className="py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Atualizar Cartão
              </button>

              <button
                onClick={handleRemoveCard}
                disabled={removingCard}
                className="py-3 bg-red-50 text-red-700 font-semibold rounded-lg hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {removingCard ? 'A remover...' : 'Remover Cartão'}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Ao remover o cartão, não poderá desbloquear novas leads até adicionar outro método de pagamento.
            </p>
          </div>
        ) : (
          <>
            {showUpdateForm && (
              <button
                onClick={() => setShowUpdateForm(false)}
                className="mb-4 text-sm text-gray-600 hover:text-gray-900"
              >
                ← Voltar
              </button>
            )}
            <Elements stripe={stripePromise}>
              <CardForm />
            </Elements>
          </>
        )}
      </div>

      {/* Histórico de Cobranças */}
      {charges.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-8 mt-8 border border-primary/10">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Histórico de Cobranças</h2>

          {loadingCharges ? (
            <div className="text-center text-gray-600">A carregar...</div>
          ) : (
            <div className="space-y-4">
              {charges.map((charge, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="font-medium text-gray-900">{charge.full_name}</p>
                    <p className="text-sm text-gray-600">
                      {new Date(charge.created_at).toLocaleDateString('pt-PT', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-primary">{charge.price}€</div>
                    <div className="text-xs text-green-600 font-medium">Pago</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingPage;
